import { NgClass } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';

import { TSourceDetail } from '@core/interfaces/TMessage';

import { Conversation } from '../../../infrastructure/models/Conversation';
import { AgentConversationService } from '../../../services/agent-conversation.service';
import {
  TAdaptedMessageV2,
  TAdaptedQuizQuestion,
  TAdaptedReferenceV3,
  TReferenceToDisplay,
} from '../../../types/types';
import { BotsService } from '@shared/services/bots.service';
import { ArrayUtils } from '@shared/utils/arrayUtils';

import { QuizAnswer, QuizQuestion } from '../../../../api/dtos/conversation.dto';
import { QuizReferenceComponent } from '../quiz-reference/quiz-reference.component';
import { ReferencesComponent } from '../references/references.component';
import { SourcePreviewModalComponent } from '../source-preview/modal/source-preview-modal.component';

@Component({
    selector: 'app-bot-quiz-answers',
    templateUrl: './bot-quiz-answers.component.html',
    styleUrls: ['./bot-quiz-answers.component.scss'],
    standalone: true,
    imports: [MatButton, NgClass, QuizReferenceComponent, ReferencesComponent]
})
export class BotQuizAnswersComponent implements OnInit, OnChanges {
  protected questions: TAdaptedQuizQuestion[] = [];
  protected currentQuestionIndex = 1;
  protected questionQty = 0;
  protected showMoreQuestions = true;
  protected isSavingAnswer = false;
  protected isHistoricalBatch = false; // Track if this batch is from history
  private messageData: TAdaptedMessageV2 | undefined;

  @Input() set message(value: TAdaptedMessageV2) {
    this.messageData = value;
  }

  @Input() sourcesDetails: TSourceDetail[] = [];

  @Input() asResults = false;

  @Input() messageIndex = 0;
  @Input() conversation: Conversation | null = null;

  /** Exposed for app-references (per-number external lists). */
  protected get messageForReferences(): TAdaptedMessageV2 | undefined {
    return this.messageData;
  }

  constructor(
    private _botService: BotsService,
    private _dialog: MatDialog,
    private _agentConversationService: AgentConversationService
  ) {}

  /**
   * Check if this is the first quiz message in the conversation
   * Used to show the "Quiz" badge only once
   */
  protected get isFirstQuizBatch(): boolean {
    if (!this.conversation || !this.messageData) {
      return true; // Default to showing badge
    }

    // Find the first quiz-type message
    const firstQuizMessageIndex = this.conversation.messages.findIndex(msg => msg.type === 'quiz');

    // Show badge only if this is the first quiz message
    return firstQuizMessageIndex === this.messageIndex;
  }

  ngOnInit(): void {
    this.initializeQuestions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize when message data changes (new questions added)
    if (changes.messageData && !changes.messageData.firstChange) {
      this.initializeQuestions();
    }
  }

  private initializeQuestions(): void {
    if (!this.messageData) {
      return;
    }
    const quzPar = this.messageData.paragraphs[this.messageData.paragraphs.length - 1];
    if (quzPar?.quizQuestions && quzPar.quizQuestions.length > 0) {
      this.questions = this.shuffleQuiz(quzPar.quizQuestions);
      this.questionQty = quzPar.quizQuestions.length;

      // Check if this is a historical (already answered) quiz batch
      // by checking if any question has correctAnswered set
      this.isHistoricalBatch = this.questions.some(q => q.correctAnswered !== undefined);
      const shouldShowAsResults = this.asResults && this.isHistoricalBatch;

      if (!shouldShowAsResults) {
        // Live mode: show questions one at a time
        this.addAssistantQuestion(quzPar.quizQuestions[0].question);
        this.currentQuestionIndex = 1; // Start with 1 question in live mode
        // Re-number inline references sequentially for live quiz mode
        this.renumberLiveQuizReferences();
      } else {
        // Results mode (historical): show ALL questions at once
        this.currentQuestionIndex = this.questionQty;
        // Note: In results mode, inline refs are already re-numbered by history.adapter.ts
      }
    } else {
      this.questionQty = 0;
      this.currentQuestionIndex = 0;
      return;
    }
  }

  checkAnswer(question: TAdaptedQuizQuestion, answer: QuizAnswer): void {
    // Prevent rapid clicking - ignore if already saving
    if (this.isSavingAnswer) {
      return;
    }

    this.isSavingAnswer = true;
    question.disabled = true;
    answer.selected = true;
    question.correctAnswered = answer.correct === answer.selected;
    if (this.currentQuestionIndex < this.questionQty) {
      this.currentQuestionIndex++;
    }
    this.sendMessages(question, answer);
  }

  getCorrectAnswerForQuestion(question: TAdaptedQuizQuestion): string {
    const answer = question.answers.find(answer => answer.correct);
    return answer?.answer || '';
  }

  private shuffleQuiz(quiz: TAdaptedQuizQuestion[]): TAdaptedQuizQuestion[] {
    return quiz.map((question: QuizQuestion) => {
      return {
        ...question,
        answers: ArrayUtils.shuffle(question.answers),
      };
    });
  }

  private addAssistantQuestion(message: string): void {
    if (this.conversation?.id) {
      this._botService
        .addAssistantQuestion(
          this.conversation.id,
          JSON.stringify({ type: 'assistant', data: message })
        )
        .subscribe({
          error: err => {
            console.error('[Quiz] Failed to persist question prompt:', err);
          },
        });
    }
  }

  private sendMessages(question: TAdaptedQuizQuestion, answer: QuizAnswer): void {
    const dataForResults = {
      type: 'quiz',
      data: {
        question: question.question,
        answers: question.answers,
        good_answer_reply: question.good_answer_reply,
        wrong_answer_reply: question.wrong_answer_reply,
        good_answer_reply_references: question.good_answer_reply_references,
        wrong_answer_reply_references: question.wrong_answer_reply_references,
        adaptedAnswer: question.adaptedAnswer,
        correctAnswered: question.correctAnswered,
        disabled: true,
      },
    };

    const assistantMessage = {
      type: 'assistant',
      data: this.generateAssistantMessage(question, answer),
    };

    const userMessage = {
      type: 'user',
      data: answer.answer,
    };

    if (this.conversation?.id) {
      this._botService.sendAnswerAndNextQuestion(
        {
          conversationId: this.conversation.id,
          userAnswer: JSON.stringify(userMessage),
          correct: answer.correct,
          assistantMessage: JSON.stringify(assistantMessage),
          dataForResultsStore: JSON.stringify(dataForResults),
        },
        () => {
          // Success callback - re-enable clicking
          this.isSavingAnswer = false;
        },
        () => {
          // Error callback - re-enable clicking
          this.isSavingAnswer = false;
        }
      );
    } else {
      this.isSavingAnswer = false;
    }
  }

  canAskForMoreQuestions(): boolean {
    return this.showMoreQuestions && this.currentQuestionIndex === this.questionQty;
  }

  loadMoreQuestions() {
    this.showMoreQuestions = false;
    this._agentConversationService
      .askQuestionToAgent({ message: '' })
      .then(() => {
        this.showMoreQuestions = false;
      })
      .catch(() => {
        this.showMoreQuestions = true;
      });
  }

  getFeedback() {
    this.showMoreQuestions = false;
    this._agentConversationService
      .askQuestionToAgent({
        message: '',
        request_type: 'get_feedback',
      })
      .catch(() => {
        // Re-show buttons if feedback request fails
        this.showMoreQuestions = true;
      });
  }

  protected getAnswerClass(question: TAdaptedQuizQuestion, answer: QuizAnswer) {
    if (this.isHistoricalBatch) {
      // Historical quiz: show all correct/incorrect answers
      return {
        correct: answer.correct,
        incorrect: answer.selected && !answer.correct,
      };
    } else {
      // Live quiz: only show correct answer after question is answered
      return {
        correct: question.disabled && answer.correct,
        incorrect: answer.selected && !answer.correct,
      };
    }
  }

  private generateAssistantMessage(question: TAdaptedQuizQuestion, answer: QuizAnswer): string {
    if (answer.correct) {
      return 'Great that\'s correct answer!';
    }
    return `Sorry, that's incorrect answer! Correct answer is ${this.getCorrectAnswerForQuestion(question)}.`;
  }

  openSourcePreviewModal(reference: TAdaptedReferenceV3, question?: TAdaptedQuizQuestion) {
    let allReferences: TAdaptedReferenceV3[] = [];

    if (question) {
      // Called from inline reference - show only this question's references
      allReferences =
        question.adaptedAnswer?.[question.correctAnswered ? 'correct' : 'incorrect'].references ??
        [];
    } else {
      // Called from bottom list - show all references from current batch (this.questions)
      this.questions.forEach(q => {
        if (q.correctAnswered !== undefined && q.adaptedAnswer) {
          const refs = q.correctAnswered
            ? q.adaptedAnswer.correct.references
            : q.adaptedAnswer.incorrect.references;
          allReferences.push(...refs);
        }
      });
    }

    const data = {
      referenceForSelectList: {
        children: allReferences,
      },
      sourceId: reference.sourceId,
      selectedReference: reference,
      conversationId: this.conversation?.id,
      resolvedReferenceDetails: allReferences
        .map(child => ({
          sourceId: child.sourceId ?? reference.sourceId!,
          embeddingId: child.referenceDto?.embeddingId,
          detail:
            this.sourcesDetails.find(
              source =>
                source._id === child.sourceId &&
                source.embeddingID === child.referenceDto?.embeddingId
            ) ??
            this.sourcesDetails.find(source => source._id === child.sourceId),
        }))
        .filter(
          (item): item is { sourceId: string; embeddingId: string | undefined; detail: TSourceDetail } =>
            !!item.detail
        ),
    };

    this._dialog.open(SourcePreviewModalComponent, {
      data,
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: ['source-preview-modal', 'slide-in-right'],
      hasBackdrop: true,
      disableClose: false,
    });
  }

  getReferencesForQuestion(questionIndex: number): TReferenceToDisplay[] {
    // Get references for a single question
    const question = this.questions[questionIndex];

    if (!question || question.correctAnswered === undefined || !question.adaptedAnswer) {
      return [];
    }

    const allRefs = question.correctAnswered
      ? question.adaptedAnswer.correct.references
      : question.adaptedAnswer.incorrect.references;

    // Safety check: ensure allRefs is a valid array
    if (!allRefs || !Array.isArray(allRefs)) {
      return [];
    }

    // Group references by sourceId
    const groupedBySource = new Map<string, TAdaptedReferenceV3[]>();

    allRefs.forEach(ref => {
      if (ref.sourceId) {
        if (!groupedBySource.has(ref.sourceId)) {
          groupedBySource.set(ref.sourceId, []);
        }
        groupedBySource.get(ref.sourceId)!.push(ref);
      }
    });

    // Convert to TReferenceToDisplay format
    return Array.from(groupedBySource.entries()).map(([sourceId, children]) => {
      const sourceDetail = this.sourcesDetails.find(s => s._id === sourceId);

      return {
        originalSource: sourceDetail || {
          _id: sourceId,
          name: 'Unknown Source',
          folder: [],
          references: '',
        },
        children,
      };
    });
  }

  getAllGroupedReferences(): TReferenceToDisplay[] {
    // Collect all references from all answered questions in this message
    const allRefs: TAdaptedReferenceV3[] = [];

    this.questions.forEach(question => {
      if (question.correctAnswered !== undefined && question.adaptedAnswer) {
        const answerRefs = question.correctAnswered
          ? question.adaptedAnswer.correct.references
          : question.adaptedAnswer.incorrect.references;

        allRefs.push(...answerRefs);
      }
    });

    // References are already numbered correctly (by history.adapter or renumberLiveQuizReferences)
    // Just use them as-is
    const refsToUse = allRefs;

    // Group all references by sourceId
    const groupedBySource = new Map<string, TAdaptedReferenceV3[]>();

    refsToUse.forEach(ref => {
      if (ref.sourceId) {
        if (!groupedBySource.has(ref.sourceId)) {
          groupedBySource.set(ref.sourceId, []);
        }
        groupedBySource.get(ref.sourceId)!.push(ref);
      }
    });

    // Convert to TReferenceToDisplay format
    return Array.from(groupedBySource.entries()).map(([sourceId, children]) => {
      // Find source details from conversation
      const sourceDetail = this.sourcesDetails.find(s => s._id === sourceId);

      return {
        originalSource: sourceDetail || {
          _id: sourceId,
          name: 'Unknown Source',
          folder: [],
          references: '',
        },
        children,
      };
    });
  }

  hasAnyAnsweredQuestions(): boolean {
    return this.questions.some(q => q.correctAnswered !== undefined);
  }

  /**
   * Get the highest reference number from all previous QUIZ messages in the conversation
   * Returns 0 if this is the first quiz batch
   * NOTE: Only counts quiz answer references, NOT init message references (they're numbered separately)
   */
  private getLastReferenceNumberFromConversation(): number {
    if (!this.conversation || !this.messageData) {
      return 0;
    }

    let highestRefIndex = 0;

    // Find current message index (returns -1 if not found yet)
    const currentMessageIndex = this.conversation.messages.findIndex(
      msg => msg._id === this.messageData!._id
    );

    // If message not found yet (brand new), check all messages
    // Otherwise, only check messages BEFORE the current one
    const messagesToCheck =
      currentMessageIndex === -1
        ? this.conversation.messages
        : this.conversation.messages.slice(0, currentMessageIndex);

    messagesToCheck.forEach(msg => {
      if (msg.type === 'quiz' && msg.paragraphs[0]?.quizQuestions) {
        msg.paragraphs[0].quizQuestions.forEach((question: TAdaptedQuizQuestion) => {
          if (question.adaptedAnswer) {
            // Check both correct and incorrect references
            const correctRefs = question.adaptedAnswer.correct?.references || [];
            const incorrectRefs = question.adaptedAnswer.incorrect?.references || [];

            [...correctRefs, ...incorrectRefs].forEach(ref => {
              if (ref.listPositionIndex && ref.listPositionIndex > highestRefIndex) {
                highestRefIndex = ref.listPositionIndex;
              }
            });
          }
        });
      }
    });

    return highestRefIndex;
  }

  private renumberLiveQuizReferences(): void {
    // Find the highest reference number from previous quiz messages in the conversation
    let globalRefIndex = this.getLastReferenceNumberFromConversation() + 1;

    // In live mode, number correct AND incorrect references with SAME numbers
    // because the user will only see one set (whichever answer they choose)
    this.questions.forEach(question => {
      if (question.adaptedAnswer) {
        const startIndex = globalRefIndex;

        // Re-number correct answer references
        if (question.adaptedAnswer.correct?.references) {
          let localIndex = startIndex;
          question.adaptedAnswer.correct.references.forEach(ref => {
            ref.listPositionIndex = localIndex++;
          });
          globalRefIndex = localIndex; // Update global index for next question
        }

        // Re-number incorrect answer references with SAME numbers
        if (question.adaptedAnswer.incorrect?.references) {
          let localIndex = startIndex; // Start from same index as correct
          question.adaptedAnswer.incorrect.references.forEach(ref => {
            ref.listPositionIndex = localIndex++;
          });
        }
      }
    });
  }

  shouldShowReferences(): boolean {
    // Show references after each answered quiz (both live and results mode)
    return this.hasAnyAnsweredQuestions();
  }

  getAllConversationQuizReferences(): TReferenceToDisplay[] {
    // Collect ALL references from ALL quiz messages in the conversation
    const allRefs: TAdaptedReferenceV3[] = [];

    if (!this.conversation) {
      return [];
    }

    this.conversation!.messages.forEach(msg => {
      if (msg.type === 'quiz' && msg.paragraphs[0]?.quizQuestions) {
        msg.paragraphs[0].quizQuestions.forEach((question: TAdaptedQuizQuestion) => {
          if (question.correctAnswered !== undefined && question.adaptedAnswer) {
            const answerRefs = question.correctAnswered
              ? question.adaptedAnswer.correct.references
              : question.adaptedAnswer.incorrect.references;

            // Safety check: only push if answerRefs exists and is an array
            if (answerRefs && Array.isArray(answerRefs)) {
              allRefs.push(...answerRefs);
            }
          }
        });
      }
    });

    // Re-number all references sequentially (1, 2, 3, 4, 5, 6...)
    const renumberedRefs = allRefs.map((ref, index) => ({
      ...ref,
      listPositionIndex: index + 1,
    }));

    // Group all references by sourceId
    const groupedBySource = new Map<string, TAdaptedReferenceV3[]>();

    renumberedRefs.forEach(ref => {
      if (ref.sourceId) {
        if (!groupedBySource.has(ref.sourceId)) {
          groupedBySource.set(ref.sourceId, []);
        }
        groupedBySource.get(ref.sourceId)!.push(ref);
      }
    });

    // Convert to TReferenceToDisplay format
    return Array.from(groupedBySource.entries()).map(([sourceId, children]) => {
      // Find source details from conversation
      const sourceDetail = this.sourcesDetails.find(s => s._id === sourceId);

      return {
        originalSource: sourceDetail || {
          _id: sourceId,
          name: 'Unknown Source',
          folder: [],
          references: '',
        },
        children,
      };
    });
  }
}
